/**
 * Clinica Storage Utility
 * Centralized storage for clinica-related state using localforage (IndexedDB).
 * An in-memory cache keeps reads synchronous; writes persist to IndexedDB
 * in the background (fire-and-forget).
 *
 * IMPORTANT: Call ClinicaStorageUtil.init() before app render.
 *
 * Managed keys (localforage):
 *   - clinicaSurveyState  → { ken, equipmentType, formType }
 *   - KEN                 → standalone KEN value (legacy)
 *   - clinicaForm         → escalator form selection
 *   - iotData_<ken>       → cached IOT equipment data per KEN
 *
 * Keys still on localStorage (not managed here):
 *   - progress.flow / progress.currentIndex
 *   - draft keys (clinica-<ken>, siteSurvey-<ken>)
 */

import localforage from 'localforage';

const store = localforage.createInstance({
  name: 'clinica-storage',
  storeName: 'clinica_state',
});

// In-memory cache — hydrated from localforage on init()
const cache = {};

function cacheGet(key) {
  return cache[key] ?? null;
}

function cacheSet(key, value) {
  cache[key] = value;
  store.setItem(key, value).catch((e) =>
    console.warn(`[ClinicaStorage] Failed to persist "${key}"`, e)
  );
}

function cacheRemove(key) {
  delete cache[key];
  store.removeItem(key).catch((e) =>
    console.warn(`[ClinicaStorage] Failed to remove "${key}"`, e)
  );
}

const STORAGE_KEY = 'clinicaSurveyState';
const DEFAULT_STATE = { ken: null, equipmentType: null, formType: null };

export const ClinicaStorageUtil = {

  // ─── Initialization (call once before app render) ─────────

  async init() {
    const keys = await store.keys();
    for (const key of keys) {
      cache[key] = await store.getItem(key);
    }
  },

  // ─── Unified survey state ─────────────────────────────────

  getState() {
    return cacheGet(STORAGE_KEY) || { ...DEFAULT_STATE };
  },

  getKEN() {
    return this.getState().ken;
  },

  getEquipmentType() {
    return this.getState().equipmentType;
  },

  getFormType() {
    return this.getState().formType;
  },

  setKEN(ken) {
    const state = this.getState();
    if (state.ken !== ken) {
      const newState = { ken, equipmentType: null, formType: null };
      cacheSet(STORAGE_KEY, newState);
      return newState;
    }
    return state;
  },

  setEquipmentType(equipmentType) {
    const state = this.getState();
    const newState = {
      ...state,
      equipmentType,
      formType: state.equipmentType !== equipmentType ? '' : state.formType,
    };
    cacheSet(STORAGE_KEY, newState);
    return newState;
  },

  setFormType(formType) {
    const state = this.getState();
    const newState = { ...state, formType };
    cacheSet(STORAGE_KEY, newState);
    return newState;
  },

  hasKENChanged(currentKEN) {
    const storedKEN = this.getKEN();
    return storedKEN && storedKEN !== currentKEN;
  },

  // ─── Legacy standalone KEN key ────────────────────────────

  getLegacyKEN() {
    return cacheGet('KEN') || '';
  },

  setLegacyKEN(value) {
    if (value) {
      cacheSet('KEN', value);
    }
  },

  removeLegacyKEN() {
    cacheRemove('KEN');
  },

  // ─── Clinica form (escalator form selection) ──────────────

  getClinicaForm() {
    return cacheGet('clinicaForm') || '';
  },

  setClinicaForm(value) {
    cacheSet('clinicaForm', value);
  },

  removeClinicaForm() {
    cacheRemove('clinicaForm');
  },

  // ─── IOT data cache ───────────────────────────────────────

  getIOTData(ken) {
    return cacheGet(`iotData_${ken}`) || null;
  },

  setIOTData(ken, data) {
    cacheSet(`iotData_${ken}`, data);
  },

  clearAllIOTData() {
    Object.keys(cache).forEach((key) => {
      if (key.startsWith('iotData_')) {
        cacheRemove(key);
      }
    });
  },

  // ─── Full clear (all clinica-related keys) ────────────────

  clear() {
    cacheRemove(STORAGE_KEY);
    this.removeLegacyKEN();
    this.removeClinicaForm();
    cacheRemove('clinicaOption');
    this.clearAllIOTData();
    // Progress keys remain on localStorage
    localStorage.removeItem('progress.flow');
    localStorage.removeItem('progress.currentIndex');
  },
};
